import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Search, Eye, Package, Clock, CheckCircle, XCircle, Truck } from 'lucide-react';
import api from '@/lib/api';
import { formatDateTimeShort } from '@/lib/datetime';

const STATUS_MAP = {
    pending: { label: 'Pendiente', icon: Clock, color: 'text-yellow-400' },
    confirmed: { label: 'Confirmado', icon: CheckCircle, color: 'text-blue-400' },
    preparing: { label: 'Preparando', icon: Package, color: 'text-purple-400' },
    ready: { label: 'Listo', icon: Truck, color: 'text-green-400' },
    delivered: { label: 'Entregado', icon: CheckCircle, color: 'text-white' },
    cancelled: { label: 'Cancelado', icon: XCircle, color: 'text-red-400' },
};

export default function AdminStoreOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [gymFilter, setGymFilter] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [gyms, setGyms] = useState([]);

    useEffect(() => {
        const fetchGyms = async () => {
            try {
                const res = await api.get('/admin/gyms?per_page=200');
                setGyms(res.data.data || []);
            } catch {}
        };
        fetchGyms();
    }, []);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const params = { search, page, per_page: 10 };
            if (statusFilter) params.status = statusFilter;
            if (gymFilter) params.gym_id = gymFilter;
            const res = await api.get('/admin/store/orders', { params });
            setOrders(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [search, statusFilter, gymFilter, page]);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    const openDetail = async (order) => {
        try {
            const res = await api.get(`/admin/store/orders/${order.id}`);
            setSelectedOrder(res.data.data);
        } catch {
            setSelectedOrder(order);
        }
        setDetailOpen(true);
    };

    const statusBadge = (status) => {
        const s = STATUS_MAP[status] || { label: status, icon: Clock, color: 'text-white/60' };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status === 'delivered' ? 'bg-white text-black' : 'bg-white/10 text-white'}`}>
                <s.icon className={`h-3 w-3 ${s.color}`} />
                {s.label}
            </span>
        );
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Pedidos de Tienda</h1>
            </div>

            <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                <CardHeader>
                    <div className="flex flex-wrap items-center gap-2">
                        <Search className="h-4 w-4 text-white/60" />
                        <Input placeholder="Buscar por N° de pedido o gym..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="max-w-sm bg-[#0a0a0a] border-[#3a3a3f] text-white placeholder:text-white/40 focus-visible:ring-white" />
                        <select className="h-9 rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 text-sm text-white" value={gymFilter} onChange={e => { setGymFilter(e.target.value); setPage(1); }}>
                            <option value="" className="bg-[#0a0a0a] text-white">Todos los gimnasios</option>
                            {gyms.map(g => (
                                <option key={g.id} value={g.id} className="bg-[#0a0a0a] text-white">{g.name}</option>
                            ))}
                        </select>
                        <select className="h-9 rounded-md border border-[#3a3a3f] bg-[#0a0a0a] px-3 text-sm text-white" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
                            <option value="" className="bg-[#0a0a0a] text-white">Todos los estados</option>
                            {Object.entries(STATUS_MAP).map(([key, s]) => (
                                <option key={key} value={key} className="bg-[#0a0a0a] text-white">{s.label}</option>
                            ))}
                        </select>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : orders.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron pedidos.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[700px]">
                                    <thead>
                                        <tr className="border-b border-[#3a3a3f]">
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">N° Pedido</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Gimnasio</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Productos</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Total</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Estado</th>
                                            <th className="text-left py-3 px-2 font-medium text-white/60 uppercase tracking-wider hidden sm:table-cell">Fecha</th>
                                            <th className="text-right py-3 px-2 font-medium text-white/60 uppercase tracking-wider">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {orders.map(order => (
                                            <tr key={order.id} className="border-b border-[#3a3a3f] last:border-0 hover:bg-white/5">
                                                <td className="py-3 px-2 font-mono text-xs text-white font-medium">{order.order_number}</td>
                                                <td className="py-3 px-2 text-white/60">{order.gym?.name || '-'}</td>
                                                <td className="py-3 px-2 text-white/60">{order.items_count}</td>
                                                <td className="py-3 px-2 font-semibold text-white">${parseFloat(order.total || 0).toFixed(2)}</td>
                                                <td className="py-3 px-2">{statusBadge(order.status)}</td>
                                                <td className="py-3 px-2 text-white/60 hidden sm:table-cell">{formatDateTimeShort(order.created_at)}</td>
                                                <td className="py-3 px-2 text-right">
                                                    <Button variant="ghost" size="icon" onClick={() => openDetail(order)} title="Ver detalle" className="text-white hover:bg-white/5">
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center gap-2 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Anterior</Button>
                                    <span className="flex items-center text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} className="border-[#3a3a3f] text-white hover:bg-white/5">Siguiente</Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#3a3a3f] text-white sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
                            <Package className="h-5 w-5" />
                            Pedido {selectedOrder?.order_number}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedOrder && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Gimnasio</Label>
                                    <p className="text-sm text-white mt-1">{selectedOrder.gym?.name || '-'}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Estado</Label>
                                    <div className="mt-1">{statusBadge(selectedOrder.status)}</div>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Total</Label>
                                    <p className="text-sm font-semibold text-white mt-1">${parseFloat(selectedOrder.total || 0).toFixed(2)}</p>
                                </div>
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Pago</Label>
                                    <p className="text-sm text-white/60 mt-1 capitalize">{selectedOrder.payment_method || '-'} — {selectedOrder.payment_status || '-'}</p>
                                </div>
                            </div>
                            {selectedOrder.notes && (
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Notas</Label>
                                    <p className="text-sm text-white/80 mt-1">{selectedOrder.notes}</p>
                                </div>
                            )}
                            <div>
                                <Label className="text-white/60 uppercase tracking-wider text-xs">Productos</Label>
                                <div className="space-y-2 mt-1">
                                    {selectedOrder.items?.map(item => (
                                        <div key={item.id} className="flex items-center justify-between p-2 rounded bg-white/5 border border-[#3a3a3f]">
                                            <div>
                                                <p className="text-sm text-white">{item.product?.name || item.product_name || 'Producto'}</p>
                                                <p className="text-xs text-white/60">${parseFloat(item.product_price || 0).toFixed(2)} x {item.quantity}</p>
                                            </div>
                                            <span className="text-sm font-semibold text-white">${(parseFloat(item.product_price || 0) * parseInt(item.quantity || 0)).toFixed(2)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            {selectedOrder.status_histories?.length > 0 && (
                                <div>
                                    <Label className="text-white/60 uppercase tracking-wider text-xs">Historial</Label>
                                    <div className="space-y-1 mt-1">
                                        {selectedOrder.status_histories.map(h => (
                                            <div key={h.id} className="flex items-center justify-between text-xs text-white/60">
                                                <span>
                                                    {STATUS_MAP[h.from_status]?.label || h.from_status || '-'} → {STATUS_MAP[h.to_status]?.label || h.to_status}
                                                    {h.changed_by?.name && <span className="text-white/40 ml-1">por {h.changed_by.name}</span>}
                                                </span>
                                                <span>{formatDateTimeShort(h.created_at)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDetailOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
